import { dotProduct, hypotenuse2D } from '@paragon/utilities';

import { DIRECTION_VECTOR_TOLERANCE } from '../Direction2D';
import { getPointAlongLine } from './line';
import { segmentContainsPointKnownToBeOnLine } from './segment';
import { Segment2Struct, Vec2Struct } from './types';

/**
 * Allows you to normalize a `Vec2Struct` to allow it to be used as a direction.
 *
 * Only intended for use in performance-critical code where you need to avoid heap allocations.
 */
export function normalizeDirectionVec(direction: Vec2Struct) {
  const magnitude = hypotenuse2D(direction.x, direction.y);
  if (magnitude > DIRECTION_VECTOR_TOLERANCE) {
    direction.x /= magnitude;
    direction.y /= magnitude;
  } else {
    direction.x = 0;
    direction.y = 0;
  }
}

// temp variables for use by projectPointOnLineSegment. DO NOT EXPORT OR USE EXTERNALLY IN ANY WAY.
const _line = { basePoint: { x: 0, y: 0 }, direction: { x: 0, y: 0 } };
const _hypotenuse = { x: 0, y: 0 };
const _hypotenuseDirection = { x: 0, y: 0 };

/**
 * A no-allocation version of `Point2D#projectOntoLineSegment`.
 *
 * Only intended for use in performance-critical code where you need to avoid heap allocations.
 */
export function projectPointOnLineSegment(point: Vec2Struct, segment: Segment2Struct, result: Vec2Struct): boolean {
  _line.basePoint.x = segment.basePoint.x;
  _line.basePoint.y = segment.basePoint.y;

  _line.direction.x = segment.endPoint.x - segment.basePoint.x;
  _line.direction.y = segment.endPoint.y - segment.basePoint.y;
  normalizeDirectionVec(_line.direction);

  _hypotenuse.x = point.x - _line.basePoint.x;
  _hypotenuse.y = point.y - _line.basePoint.y;

  _hypotenuseDirection.x = _hypotenuse.x;
  _hypotenuseDirection.y = _hypotenuse.y;
  normalizeDirectionVec(_hypotenuseDirection);

  const directionsDotProduct = dotProduct(
    _hypotenuseDirection.x,
    _hypotenuseDirection.y,
    _line.direction.x,
    _line.direction.y,
  );
  const hypotenuseMagnitude = hypotenuse2D(_hypotenuse.x, _hypotenuse.y);

  getPointAlongLine(_line, directionsDotProduct * hypotenuseMagnitude, result);

  return segmentContainsPointKnownToBeOnLine(segment, result);
}
